import {
  bindBootAccount,
  captureBootSignal,
  setBootStage,
  getBootSnapshot,
  recoverBoot
} from './boot-budget.js';
import {
  fetchRoomById,
  inspectInitialRoomInventory,
  listOrganizations,
  listRooms
} from '#~/api/agent/index.js';
import { ensureDefaultOrgAndRoom } from '#~/account/bootstrap.js';
import { loadMembers } from '#~/api/agent/registry.js';
import type { ApiError } from '#~/api/index.js';
import { toHexFromId } from '#~/api/index.js';
import { ApiError as ApiErrorClass } from '#~/api/types.js';
import { EVENT_NAMES, useAnalytics } from '#~/analytics/index.js';
import { createStudioBootstrapTracker } from '#~/analytics/domains/studio-bootstrap.js';
import { useAuth0 } from '#~/auth/index.js';
import { authStore } from '#~/auth/store.js';
import { toOrganizationMap, toRoomRecordMap } from '#~/auth/registry-transformers.js';
import { config } from '#~/config/index.js';
import { useDepot } from '#~/depot/hooks.js';
import { depot } from '#~/depot/index.js';
import {
  initIdentity,
  resolveLinkedAgentId,
  useIdentityState,
  useIdentityStore
} from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { useLastAccountIdPref } from '#~/room/hook_last_id.js';
import { useLastRoomIdPref } from '#~/room/hooks.js';
import type { Room } from '@studio/models/room';
import { unwrap } from '@studio/js-utils/async';
import { useAsyncEffect, useLatestFn } from '@studio/react-utils/hooks';
import type { AgentRegistryGetRoomResponse } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import { useEffect, useMemo, useRef, useState } from 'react'; // useRef: Tyrus' bootstrap-resolved latch
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { Paths } from '../constants.js';
import { useAutoLinkBootstrap } from '../verify-auto-link/v2/auto-link-bootstrap-context.js';
import { publishBootLifecycleFailure } from './bootstrap-analytics.js';
import { loadAccountStudioInventory } from './account-studio-inventory-loader.js';
import { reconcilePendingAccountStudioCreate } from '#~/routes/select-studio/account-studio-operations.js';
import { logger } from '#~/logger/index.js';
// Stage injection shares the entry-owned deadline; it does not arm a Root timer.
import { resolveE2EBootHangStage, type BootStage } from './boot-watchdog.js';
import {
  AccountBootstrapCoordinator,
  joinInFlightZeroStudioAutoLink,
  shouldEnableBootstrapWatchdog,
  type FencedAccountBootstrapDecision
} from './account-bootstrap-decision.js';
// Spec 4 (D1): per-document startup attribution. Pure instrumentation — every
// call below records a timestamp and returns; none of them yields, throws, or
// changes a boot decision. The recorder has no app imports of its own, so this
// adds no dependency to the boot graph being measured.
//
// MERGE DECISION (ENG-4720 x ENG-4722): Spec 4 also imported the Root-owned boot
// watchdog (createBootWatchdog / resolveBootDeadlineMs / BootWatchdog) and the
// lifecycle bookkeeping (claimBootLifecycleOnce / classifyAppBootErrorType /
// persistBootStartedAtMs / readBootStartedAtMs / getBootId) so it could measure
// them. ENG-4720 RETIRED both from this file — the deadline is owned by the app
// entry's boot budget now — so those imports are dropped rather than carried as
// dead weight. The instrumentation of the surviving flow stays.
import {
  enterRendererPhase,
  finishRendererLane,
  markRendererMilestone
} from '#~/boot/renderer-startup.js';
import { timeBootHop } from '#~/logger/forward.js';
// ENG-4154 / ENG-4685 remedy for a local Bridge linked under another account:
// vendor-neutral sidecar unlink from the boot path (the auto-link controller's
// own copy only runs on the unlinked zero-Studio path, so it never reaches
// this state), plus the typed error Root maps to the D3 copy + Sign out.
import { attemptCrossAccountUnlinkRecovery } from './cross-account-recovery.js';
import { CrossAccountBootError } from './cross-account-boot-error.js';
// ENG-4175: first-login segmentation wizard gate + shown-once backstop.
import { usePrequalGate } from './use-prequal-gate.js';
// QA Hive ITG 2026-08-10: bounded adoption polls that wait out registry
// read-replica lag on the room/org that the default-studio bootstrap just
// created (see root-room-sync.ts for the mechanism narrative).
import {
  awaitAutoLinkSettle,
  awaitBootstrapOrgVisibility,
  awaitBootstrapRoomAdoption,
  deriveDefaultStudioName
} from './root-room-sync.js';
import { AUTO_LINK_ROOM_SYNC_TIMEOUT_MS } from './boot-watchdog.js';
import { completeRoomOrganizations } from '#~/auth/registry-completeness.js';
import {
  resolveAccountBootstrapRoutingMode,
  toAccountBootstrapInput
} from './account-bootstrap-input.js';
import { isZeroStudioDecision, runRootZeroStudioBootstrap } from './root-zero-studio-bootstrap.js';
import {
  mergeAuthoritativeBootstrapOrganizations,
  resolveInitialBootstrapInventory,
  resolveInventoryChangedBootstrapInventory,
  resolveSuccessfulBootstrapInventory,
  shouldUseAuthoritativeLinkedRoomBoot
} from './authoritative-bootstrap-inventory.js';
import type {
  BridgeSetupCapabilities,
  EnsureInitialRoomInventoryOrganization
} from '#~/api/agent/registry.js';
import type { EnsureInitialRoomInventoryRoom } from '#~/api/agent/registry.js';
import { toAccountStudioPickerState } from '../select-studio/bootstrap-state.js';

// `listRooms` already owns the bounded transport retry. Root adds only a
// per-call deadline; another outer retry would multiply the request budget.
const INVENTORY_ATTEMPTS = 1;
// The deadline must not undercut API Gateway's 29s integration cap: below it
// the client abandons a listing the server is about to answer and fails the
// boot instead. ITG rooms.list ran 15–23s on 7.5% of invocations (1,808 of
// 24,041, CloudWatch 2026-09-01), never past 29s — at 15s that failed roughly
// one boot per e2e run ("Unable to load account", runs 33531841340 /
// 33536611420), while main's `listRoomsWithBootRetry` path, which has no
// client deadline, booted slowly through the same latency. A 504 still fails
// fast: the transport does not retry gateway timeouts (ENG-4250).
// Per rule: reference-rules-in-comments (ai.knowledge/global).
const API_GATEWAY_INTEGRATION_TIMEOUT_MS = 29_000;
const INVENTORY_FETCH_TIMEOUT_MS = API_GATEWAY_INTEGRATION_TIMEOUT_MS + 1_000;

// Poll cadence for the bounded room/org adoption waits below. The deadline
// (AUTO_LINK_ROOM_SYNC_TIMEOUT_MS) is shared with the boot watchdog derivation
// in boot-watchdog.ts so the boot deadline always exceeds the stacked waits.
const AUTO_LINK_ROOM_SYNC_POLL_INTERVAL_MS = 500;
// ENG-4154: liveness backstop for the stale-link settle-wait below, sized past
// the guard's MAX_SETUP_TIME_MS budget (120s e2e + fault-injection extension) —
// the guard aborts the controller into a terminal state at that point, so
// hitting this deadline means something is wedged and we degrade to the
// existing zero-Studio error path.
const AUTO_LINK_SETTLE_TIMEOUT_MS = 180_000;
// The settle wait reads the DEPOT mirror of the auto-link state, not the
// controller: the mirror carries the guard's timed release (`skipped` after
// the error hold), which is the only exit from the stale-link self-heal wait
// when the sidecar reboot never lands (suppressed under IRIS_E2E_TEST, lost
// reboot request). The controller itself stays on that error terminal forever.
const peekAutoLinkState = () => depot.getState().boot.autoLinkState;

type AccountBootstrapErrorState = {
  readonly accountId: string;
  readonly error: ApiError;
};

// Spec 4 (D1): distinguishes successive runs of the boot generator WITHIN one
// document. The hook re-runs on dependency changes (preferences loading, prequal
// resolving, account switch), and each run is its own attempt with its own phase
// sequence — collapsing them would hide a re-entry loop, which is one of the
// shapes a slow boot can take. Module scope rather than a ref on purpose: adding
// a React hook call to this hook breaks the ENG-4306 integration harness, which
// stubs only useState/useRef.
let rootAttemptSequence = 0;

// ENG-4419 e2e fault injection: a promise that never resolves, so the boot
// generator wedges at the chosen stage exactly as a real hang would — but
// rejects on abort so unmount/teardown still unwinds the generator cleanly
// (the AbortError is treated as a deliberate cancellation, not a failure).
// Only reachable under IRIS_E2E_TEST (resolveE2EBootHangStage returns null
// otherwise), so production boot never constructs it.
function e2eHangUntilAborted(signal: AbortSignal): Promise<never> {
  return new Promise<never>((_resolve, reject) => {
    const abort = (): void => reject(new DOMException('boot hang aborted', 'AbortError'));
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener('abort', abort, { once: true });
  });
}

export const useRootBootstrap = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, user: auth0User } = useAuth0();
  const { track } = useAnalytics();
  const studioBootstrapAnalytics = useMemo(() => createStudioBootstrapTracker(track), [track]);
  // Plain construction (no useMemo): the tracker is a cheap object of closures, and this
  // hook's integration test drives it outside a renderer where only useState/useRef are
  // stubbed — adding a new React hook call here would break that harness (ENG-4306).
  const [studioUser, produce, bootError] = useDepot(
    (state) => [state.user, state.produce, state.boot.error],
    shallow
  );
  const { requestZeroRoomAutoLink, completeWithoutAutoLink, getZeroRoomAutoLinkRunPhase } =
    useAutoLinkBootstrap();
  const identity = useIdentityState();
  const linkedAgentID = identity.status === 'linked' ? identity.agentId : undefined;
  // ENG-4175: 'loading' | 'show' | 'skip'. 'skip' for every returning user (zero
  // added latency); only a first-login account can be 'loading'/'show'.
  const prequalGate = usePrequalGate();
  const [lastRoomId, setLastRoomId, lastRoomLoaded] = useLastRoomIdPref();
  const [lastAccountId, setLastAccountId, lastAccountLoaded] = useLastAccountIdPref();
  const [errorState, setErrorState] = useState<AccountBootstrapErrorState>();
  const [isReady, setReady] = useState(false);
  const [watchdogEnabled, setWatchdogEnabled] = useState(false);
  const [coordinator] = useState(() => new AccountBootstrapCoordinator());

  // ENG-4720: the launch clock, the boot-lifecycle terminals and the deadline
  // watchdog moved to the entry-owned budget (routes/root/boot-budget.ts). Root
  // now only contributes stage facts and specific failures; one shared terminal
  // rejects obsolete or duplicate reports. Tyrus' bootstrap-resolved latch below
  // is NOT part of that lifecycle -- it guards the ENG-4052 account-bootstrap
  // event -- so it stays here.
  const hasTrackedBootstrapResolvedRef = useRef(false);

  useEffect(() => {
    initIdentity(auth0User?.sub);
    setErrorState(undefined);
    setReady(false);
    setWatchdogEnabled(false);
    hasTrackedBootstrapResolvedRef.current = false;
  }, [auth0User?.sub]);

  // Application entry owns start/deadline; Root contributes specific failures
  // and stage facts. One shared terminal rejects obsolete/duplicate reports.
  const trackBootFailed = useLatestFn((cause: unknown) => {
    publishBootLifecycleFailure({ cause });
  });

  useAsyncEffect(
    function* bootstrap(signal: AbortSignal) {
      const accountId = auth0User?.sub;
      if (accountId === undefined) return;

      const session = coordinator.begin(accountId);
      // V2 (ITG QA, in-app account switch): bind this account to the launch
      // BEFORE capturing its signal. Root is a descendant of ProfileSynchronizer,
      // so its effect runs first; after sign-out the budget is still bound to
      // the previous account, and the synchronizer's later bind restarts the
      // attempt and retires the signal captured here. Nothing re-runs this
      // generator, so the boot stalled on "Loading your studio…" while a cold
      // boot of the same state reached the cross-account screen. The bind is a
      // no-op once the account is current.
      // Per rule: settle-every-terminal-branch (ai.knowledge/global).
      bindBootAccount(accountId);
      // ENG-4720: fold the entry-owned launch signal into Tyrus' currency
      // guard instead of repeating `launchSignal.aborted` at each call site.
      // Every existing `isCurrent()` check then also fences a retired launch.
      const launchSignal = captureBootSignal();
      if (launchSignal.aborted) return;
      const isCurrent = (): boolean =>
        !signal.aborted && !launchSignal.aborted && coordinator.isCurrent(session);

      // Spec 4 (D1). The attempt lane covers the whole run; the milestone's
      // first-occurrence guard means only the first attempt of this document
      // stamps the bootstrap-entry snapshot.
      //
      // MERGE DECISION (ENG-4720 x ENG-4722): opened AFTER ENG-4720's two early
      // returns rather than as the first statement. A run that returns there
      // never boots, so opening a lane for it would record an attempt that did
      // nothing and leave the recorder to sweep it closed as 'cancelled'.
      rootAttemptSequence += 1;
      const attemptLane = `root:${rootAttemptSequence}`;
      markRendererMilestone('root-bootstrap-started');
      enterRendererPhase(attemptLane, 'started');

      let attemptClosed = false;
      // Closes THIS attempt's lane exactly once. A stale attempt that aborts
      // after a newer one started must not close the newer lane, which is why the
      // lane name is captured per run rather than read from a ref.
      const closeAttempt = (outcome: 'ok' | 'error' | 'cancelled', phase?: string): void => {
        if (attemptClosed) return;
        attemptClosed = true;
        signal.removeEventListener('abort', onAttemptAborted);
        if (phase !== undefined) enterRendererPhase(attemptLane, phase);
        finishRendererLane(attemptLane, outcome);
      };
      // Teardown/remount cancels the attempt. Registered once and removed on
      // close, so an aborted attempt leaves nothing behind. Any lane still open
      // when the document ends is swept closed by the recorder as 'cancelled' —
      // an attempt is never silently dropped.
      function onAttemptAborted(): void {
        closeAttempt('cancelled', 'aborted');
      }
      signal.addEventListener('abort', onAttemptAborted, { once: true });

      // ProfileSynchronizer owns the registry fetch that can fail before this
      // bootstrap generator reaches one of its own catch blocks. It publishes
      // that boot-blocking failure through depot.boot.error so the shared error
      // screen can render. Report the same failure through the lifecycle tracker
      // before stopping this boot attempt. Per rules: ai-code-requires-tests and
      // reference-rules-in-comments (ai.knowledge/global).
      if (bootError) {
        trackBootFailed(new Error([bootError.message, bootError.detail].filter(Boolean).join(' ')));
        closeAttempt('error', 'boot-error');
        return;
      }

      // ENG-4175: hold the post-login routing for first-login candidates until the
      // PREQUAL_WIZARD flag resolves, so we never navigate past the wizard and then
      // bounce. The bounded flag wait falls back to the vendor default: Iris on,
      // Hive off. Returning users are never 'loading'. Checked
      // before the ENG-4419 checkpoint machinery below — this is a pre-boot gate,
      // not a machine stage; the rendered form owns its human-wait lifetime.
      //
      // Ordered AFTER the depot boot-error guard above: a boot that has already
      // failed must be reported as failed, not silently held as "still deciding".
      if (prequalGate === 'loading') {
        // Diagnostic phase only: bootStageRef is deliberately NOT touched here,
        // because this is a pre-boot gate rather than a boot stage (see above).
        closeAttempt('cancelled', 'prequal-loading');
        return;
      }

      // Redirects a gated first-login-ever user to the post-auth wizard route
      // INSTEAD of the bootstrap's normal destination (agent setup / picker /
      // room). The wizard marks itself prompted on mount; marking here makes
      // Root restart with a skipped gate and overwrite this navigation.
      // Called at every terminal navigation of the account-first bootstrap so
      // no funnel is stranded (ENG-4175, rearmed onto the ENG-4052 flow).
      // The prequal route is wrapped in <BootReadyLayout>, which publishes
      // boot.isRootReady on mount (ENG-4186); setReady mirrors the other
      // terminal paths of this hook.
      const goToPrequalIfGated = (): boolean => {
        if (prequalGate !== 'show') return false;
        navigate(`/${Paths.agent.setup.prequal}${window.location.search}`);
        setReady(true);
        return true;
      };

      // ENG-4419: record the last reached stage (→ last_boot_stage) and, under
      // e2e fault injection ONLY, wedge the boot here so the CI receipt can
      // prove boot_failed(timeout) fires without real hardware. In production
      // e2eHangStage is always null (resolveE2EBootHangStage gates on
      // IRIS_E2E_TEST), so checkpoint() is pure additive instrumentation — it
      // yields nothing and does not change control flow. Per rule:
      // reference-rules-in-comments (ai.knowledge/global).
      const e2eHangStage = resolveE2EBootHangStage();
      function* checkpoint(stage: BootStage) {
        // Spec 4 (D1): phase entry is recorded BEFORE the stage is published so
        // the diagnostic span and last_boot_stage always agree. Adds no yield.
        // MERGE DECISION (ENG-4720 x ENG-4722): ENG-4720 replaced the stage ref
        // with setBootStage; the recorder call is orthogonal and rides in front.
        enterRendererPhase(attemptLane, stage);
        setBootStage(stage);
        if (e2eHangStage === stage) {
          yield e2eHangUntilAborted(signal);
        }
      }

      // MERGE DECISION (ENG-4720 x ENG-4722): ENG-4720's first stage, kept where
      // it sits on main — before the token read, so a boot that wedges waiting
      // for the linked agent still reports a stage. The Spec 4 recorder picks it
      // up for free: checkpoint() enters the lane phase before publishing.
      yield* checkpoint('awaiting-linked-agent');

      try {
        // Spec 4 (D1) deliberately does NOT span this one. `timeBootHop` is an
        // async function, so wrapping the token read interposes a promise
        // between the generator and the operation it yields — and this
        // generator's yield protocol is load-bearing: the boot terminal-outcome
        // harness drives it synchronously and matches on the yielded value's
        // identity to decide where to retire a launch. Instrumentation does not
        // get to change the shape of what a generator yields. The wait is still
        // bounded in the trail by the `awaiting-linked-agent` phase before it
        // and `listing-rooms` after.
        const token: string = yield* unwrap(getAccessTokenSilently());
        if (!isCurrent()) return;

        const failedLists = authStore.getState().registryLists;
        const resolveLoadedLists = (): void => {
          // Root's successful publication also recovers the synchronizer's
          // failed lists, but cannot dismiss a replacement account/run's error.
          for (const list of ['studios', 'organizations'] as const) {
            if (authStore.getState().registryLists[list]?.retry === failedLists[list]?.retry)
              authStore.getState().resolveRegistryList(list);
          }
        };

        const routingMode = resolveAccountBootstrapRoutingMode(isElectronApp(), config.vendor);
        const accountFirstRouting = routingMode === 'electron_iris';
        // ENG-4419: the inventory load is the boot's room-listing wait — the
        // stage the watchdog attributes if this Promise.all never settles.
        yield* checkpoint('listing-rooms');
        // Spec 4 (D1): the inventory fan-out is the boot's dominant cloud
        // wait, so it carries its own span on the attempt lane.
        // MERGE DECISION (ENG-4720 x ENG-4722): the pre-merge span wrapped
        // `listRoomsWithBootRetry`; ENG-4720 replaced that helper with this
        // bounded-attempt loader, so the span moves onto its successor and is
        // relabelled after the mechanism that now does the retrying.
        const inventoryReads = Promise.all([
          loadAccountStudioInventory({
            attempts: INVENTORY_ATTEMPTS,
            fetchStudios: () => listRooms(token),
            fetchTimeoutMs: INVENTORY_FETCH_TIMEOUT_MS,
            signal
          }),
          accountFirstRouting
            ? inspectInitialRoomInventory(token).then(
                (inventory) => ({ status: 'available', inventory }) as const,
                (cause: unknown) => ({ status: 'unavailable', cause }) as const
              )
            : Promise.resolve(undefined),
          // Identity and primary inventory are independent boot inputs. Waiting
          // for both here prevents Studio count from implying linking intent.
          // Root owns the decision to START auto-link. Waiting for its passive
          // 30s grace window here delays that same decision on every unlinked
          // boot. A forced consult reads the real sidecar without that wait.
          accountFirstRouting ? resolveLinkedAgentId(true) : Promise.resolve(undefined)
        ])
          .then(
            ([inventory, authoritativeInventory]) => [inventory, authoritativeInventory] as const
          )
          .catch((cause: unknown) => {
            if (accountFirstRouting) {
              studioBootstrapAnalytics.track(EVENT_NAMES.ROOMS_FETCH_FAILED, {
                surface: 'electron_account_bootstrap',
                picker_mode: 'undetermined',
                local_bridge_state: useIdentityStore.getState().identity.status,
                studio_count: 0,
                action: 'fetch',
                outcome: 'failure',
                error_type: 'network'
              });
            }
            throw cause;
          });
        const [loadedInventory, primaryInventory] = yield* unwrap(
          timeBootHop('root.load-studio-inventory', inventoryReads, attemptLane)
        );
        if (loadedInventory.kind === 'stale') return;
        if (loadedInventory.kind === 'error') {
          studioBootstrapAnalytics.track(EVENT_NAMES.ROOMS_FETCH_FAILED, {
            surface: 'electron_account_bootstrap',
            picker_mode: 'undetermined',
            local_bridge_state: useIdentityStore.getState().identity.status,
            studio_count: 0,
            action: 'fetch',
            outcome: 'failure',
            error_type: 'network'
          });
          throw new ApiErrorClass('Unable to load rooms after retries.', {
            cause: loadedInventory.cause
          });
        }
        let rooms = [...loadedInventory.studios];
        let authoritativeOwnedOrganizations: readonly EnsureInitialRoomInventoryOrganization[] = [];
        let authoritativeInventoryRooms: readonly EnsureInitialRoomInventoryRoom[] = [];
        // ENG-4483 W3: service capabilities ride with the primary inventory so
        // the picker's first selection decision already knows whether adding
        // to an occupied Studio is supported (the picker re-reads on refresh).
        let authoritativeCapabilities: BridgeSetupCapabilities | undefined;
        let verifiedInitialRoomId: Room['id'] | undefined;
        if (!isCurrent()) return;

        if (primaryInventory !== undefined) {
          const resolvedInventory = resolveInitialBootstrapInventory(rooms, primaryInventory);
          rooms = resolvedInventory.rooms;
          authoritativeInventoryRooms = resolvedInventory.pickerRooms;
          authoritativeOwnedOrganizations = resolvedInventory.ownedOrganizations;
          authoritativeCapabilities = resolvedInventory.capabilities;
          if (primaryInventory.status === 'unavailable') {
            console.warn(
              '[account-bootstrap] Primary inventory inspection unavailable; using nonempty replica inventory.',
              primaryInventory.cause
            );
          }
        }
        if (!accountFirstRouting && (!lastRoomLoaded || !lastAccountLoaded)) {
          return;
        }

        let preferredStudioId = lastRoomId;
        if (
          !accountFirstRouting &&
          lastAccountId !== undefined &&
          lastAccountId !== studioUser?.id
        ) {
          setLastAccountId(studioUser?.id);
          setLastRoomId(undefined);
          preferredStudioId = undefined;
        }

        const bootstrapInput = (): ReturnType<typeof toAccountBootstrapInput> =>
          toAccountBootstrapInput(rooms, useIdentityStore.getState().identity, {
            preferredStudioId,
            // Legacy Browser/Hive behavior always honors the last-room preference.
            rememberStudio: true,
            routingMode
          });

        const intentIdentity = useIdentityStore.getState().identity;
        let completedZeroBootstrap = false;
        // Hex id of the room the default-room bootstrap created THIS boot, if
        // any — the org loads below use it to wait out the same replica lag
        // for the room's organization (switcher: empty orgs → "Create a studio").
        let bootstrapCreatedRoomId: string | undefined;
        // A pass that re-ran mid-link rejoins the in-flight zero-Studio run.
        let decision: FencedAccountBootstrapDecision<AgentRegistryGetRoomResponse> =
          joinInFlightZeroStudioAutoLink(
            coordinator.decide(session, bootstrapInput()),
            getZeroRoomAutoLinkRunPhase(accountId)
          );
        setWatchdogEnabled(shouldEnableBootstrapWatchdog(decision));

        if (decision.kind === 'inventory_error') {
          throw new ApiErrorClass(`Unable to load account inventory (${decision.reason}).`);
        }
        if (
          decision.kind === 'stale' ||
          decision.kind === 'wait_for_inventory' ||
          decision.kind === 'wait_for_identity'
        ) {
          return;
        }
        if (decision.kind === 'identity_error') {
          throw new ApiErrorClass(`Unable to resolve the local Bridge (${decision.cause}).`);
        }
        if (decision.kind === 'access_empty') {
          // The local Bridge is linked to a Studio this account cannot see: a
          // warm account switch (ENG-4685 / ENG-4483 ITB #004) or a cross-account
          // pair. Attempt the account-agnostic sidecar unlink ONCE (TTL-latched,
          // see cross-account-recovery.ts) and reload: the fresh boot sees an
          // unlinked identity and takes the normal Bridge Setup path (0 Studios
          // → create, 1+ → picker). If the unlink is refused or latched, throw
          // the typed error so Root renders the D3 copy with a Sign-out escape
          // instead of the generic reload-only screen.
          // Per rule: reference-rules-in-comments (ai.knowledge/global).
          logger.warning(
            `[account-bootstrap] local Bridge ${linkedAgentID ?? '<unknown>'} is linked to a Studio outside this account's inventory (studios=${rooms.length}); attempting in-place unlink before Bridge Setup`
          );
          if (linkedAgentID !== undefined) {
            const recovered = yield* unwrap(
              attemptCrossAccountUnlinkRecovery({ agentId: linkedAgentID })
            );
            if (!isCurrent()) return;
            if (recovered) {
              logger.warning(
                '[account-bootstrap] cross-account bridge link cleared by sidecar unlink — reloading to converge on the unlinked boot path'
              );
              // ENG-4720: a recovery relaunch keeps the ORIGINAL launch budget.
              // A bare reload would hand the next attempt a fresh 307s.
              if (getBootSnapshot()) void recoverBoot('reload');
              else window.location.reload();
              return;
            }
          }
          throw new CrossAccountBootError(linkedAgentID);
        }

        if (isZeroStudioDecision(decision)) {
          const zeroResult = yield* unwrap(
            runRootZeroStudioBootstrap(decision, {
              requestAutoLink: () => requestZeroRoomAutoLink({ accountId, signal }),
              bootstrapDefaultStudio: async () => {
                const created = await ensureDefaultOrgAndRoom(
                  token,
                  deriveDefaultStudioName(studioUser)
                );
                let studios = await listRooms(token);
                // QA Hive ITG 2026-08-10 (replay 019fecc4…): the re-list above
                // is served by a read replica and can lag the create, leaving
                // the just-created room invisible — the boot then failed with
                // 'No rooms are available for this account.' ("Unable to load
                // account"; retry always worked because the room existed by
                // then). Adopt the created room deterministically: direct
                // lookup first, bounded list poll as backstop.
                // Per rule: reference-rules-in-comments (ai.knowledge/global).
                if (created?.newRoomId) {
                  studios = await awaitBootstrapRoomAdoption({
                    createdRoomId: created.newRoomId,
                    rooms: studios,
                    listRooms: () => listRooms(token),
                    fetchRoomById: (roomId) => fetchRoomById(roomId as Room['id'], token),
                    roomIdOf: (room) => toHexFromId(room.id),
                    timeoutMs: AUTO_LINK_ROOM_SYNC_TIMEOUT_MS,
                    pollIntervalMs: AUTO_LINK_ROOM_SYNC_POLL_INTERVAL_MS,
                    signal
                  });
                  // Carry the created-room id to the org loads below — the org
                  // created in the same call rides the same replica-lagged reads.
                  bootstrapCreatedRoomId = created.newRoomId;
                }
                return studios;
              },
              // ENG-4175: a gated first-login user goes to the segmentation
              // wizard INSTEAD of the download flow — the wizard's exit path
              // resumes agent setup.
              openAgentSetup: () => {
                if (!goToPrequalIfGated()) navigate(Paths.agent.setup.$);
              }
            })
          );
          if (!isCurrent()) return;

          if (zeroResult.kind === 'setup_opened') {
            setReady(true);
            return;
          }

          if (zeroResult.kind === 'default_studio') {
            rooms = [...zeroResult.studios];
            decision = coordinator.decide(session, bootstrapInput());
            setWatchdogEnabled(shouldEnableBootstrapWatchdog(decision));
          } else if (zeroResult.result.kind === 'stale') {
            return;
          } else if (zeroResult.result.state.status === 'success') {
            const successfulState = zeroResult.result.state;
            completedZeroBootstrap = true;
            const successfulInventory = yield* unwrap(
              resolveSuccessfulBootstrapInventory(
                successfulState,
                async () => listRooms(token),
                async (roomId) => fetchRoomById(roomId, token)
              )
            );
            rooms = successfulInventory.rooms;
            authoritativeOwnedOrganizations = successfulInventory.ownedOrganizations;
            if (successfulState.room !== undefined) {
              verifiedInitialRoomId = successfulState.roomId;
              authoritativeInventoryRooms = [successfulState.room];
            }
            if (!isCurrent()) return;
            const initialRoom = rooms.find(
              (room) => toHexFromId<Room['id']>(room.id) === successfulState.roomId
            );
            if (initialRoom === undefined) {
              throw new ApiErrorClass(
                'The newly created Studio is missing from account inventory.'
              );
            }
            // Silent zero-Studio bootstrap is a completed setup action, not a
            // fresh intent decision. Enter the verified room immediately.
            decision = {
              kind: 'enter_studio',
              studio: { id: successfulState.roomId, value: initialRoom }
            };
            setWatchdogEnabled(shouldEnableBootstrapWatchdog(decision));
            // This computer's Bridge id, when the identity store already reflects the link.
            const linkedIdentity = useIdentityStore.getState().identity;
            studioBootstrapAnalytics.track(EVENT_NAMES.STUDIO_CREATED_SILENT, {
              surface: 'electron_account_bootstrap',
              picker_mode: 'bridge_setup',
              local_bridge_state: 'unlinked',
              studio_count: rooms.length,
              target_link_status: 'create',
              action: 'create',
              outcome: 'success',
              studio_id: successfulState.roomId,
              ...(linkedIdentity.status === 'linked' && linkedIdentity.agentId !== undefined
                ? { agent_id: linkedIdentity.agentId }
                : {})
            });
          } else if (zeroResult.result.state.status === 'inventory_changed') {
            // A Studio appeared after Root's settled-zero snapshot but before
            // controller mutation. Refresh the authoritative inventory and run
            // the normal one/many decision; never treat the race as a boot error.
            const changedInventory = yield* unwrap(
              resolveInventoryChangedBootstrapInventory(zeroResult.result.state, async () =>
                listRooms(token)
              )
            );
            rooms = changedInventory.rooms;
            authoritativeOwnedOrganizations = changedInventory.ownedOrganizations;
            authoritativeInventoryRooms = zeroResult.result.state.rooms ?? [];
            if (!isCurrent()) return;
            decision = coordinator.decide(session, bootstrapInput());
            setWatchdogEnabled(shouldEnableBootstrapWatchdog(decision));
          } else {
            // ENG-4695: a cleared stale cross-account link is a reboot in
            // flight, not a verdict — the sidecar wipes its credentials and
            // relaunches into the fresh-install link path. Failing boot here
            // painted "Unable to load account" for the seconds before that
            // reboot. Hold on the depot mirror instead: it carries the guard's
            // timed release (`skipped` after STALE_LINK_REBOOT_GRACE_MS), which
            // is the only exit when the reboot never lands. Only then is the
            // zero-Studio outcome a real error.
            // Per rule: reference-rules-in-comments (ai.knowledge/global).
            if (
              zeroResult.result.state.status === 'error' &&
              zeroResult.result.state.reason === 'stale_link_cleared'
            ) {
              yield* checkpoint('awaiting-auto-link-settle');
              yield* unwrap(
                awaitAutoLinkSettle({
                  peekState: peekAutoLinkState,
                  autoLinkExpected: false,
                  timeoutMs: AUTO_LINK_SETTLE_TIMEOUT_MS,
                  pollIntervalMs: AUTO_LINK_ROOM_SYNC_POLL_INTERVAL_MS,
                  signal
                })
              );
              if (!isCurrent()) return;
            }
            throw new ApiErrorClass('No rooms are available for this account.');
          }
        }

        if (decision.kind === 'inventory_error') {
          throw new ApiErrorClass(`Unable to load account inventory (${decision.reason}).`);
        }
        if (
          decision.kind === 'stale' ||
          decision.kind === 'wait_for_inventory' ||
          decision.kind === 'wait_for_identity'
        ) {
          return;
        }
        if (decision.kind === 'identity_error') {
          throw new ApiErrorClass(`Unable to resolve the local Bridge (${decision.cause}).`);
        }
        if (decision.kind === 'access_empty') {
          // The local Bridge is linked to a Studio this account cannot see: a
          // warm account switch (ENG-4685 / ENG-4483 ITB #004) or a cross-account
          // pair. Attempt the account-agnostic sidecar unlink ONCE (TTL-latched,
          // see cross-account-recovery.ts) and reload: the fresh boot sees an
          // unlinked identity and takes the normal Bridge Setup path (0 Studios
          // → create, 1+ → picker). If the unlink is refused or latched, throw
          // the typed error so Root renders the D3 copy with a Sign-out escape
          // instead of the generic reload-only screen.
          // Per rule: reference-rules-in-comments (ai.knowledge/global).
          logger.warning(
            `[account-bootstrap] local Bridge ${linkedAgentID ?? '<unknown>'} is linked to a Studio outside this account's inventory (studios=${rooms.length}); attempting in-place unlink before Bridge Setup`
          );
          if (linkedAgentID !== undefined) {
            const recovered = yield* unwrap(
              attemptCrossAccountUnlinkRecovery({ agentId: linkedAgentID })
            );
            if (!isCurrent()) return;
            if (recovered) {
              logger.warning(
                '[account-bootstrap] cross-account bridge link cleared by sidecar unlink — reloading to converge on the unlinked boot path'
              );
              // ENG-4720: a recovery relaunch keeps the ORIGINAL launch budget.
              // A bare reload would hand the next attempt a fresh 307s.
              if (getBootSnapshot()) void recoverBoot('reload');
              else window.location.reload();
              return;
            }
          }
          throw new CrossAccountBootError(linkedAgentID);
        }
        if (!isZeroStudioDecision(decision) && !completedZeroBootstrap) {
          completeWithoutAutoLink({ accountId, signal });
        }

        if (!isCurrent()) return;
        if (!hasTrackedBootstrapResolvedRef.current) {
          hasTrackedBootstrapResolvedRef.current = true;
          studioBootstrapAnalytics.track(EVENT_NAMES.BRIDGE_BOOTSTRAP_RESOLVED, {
            surface: 'electron_account_bootstrap',
            picker_mode:
              decision.kind === 'choose_studio'
                ? decision.mode
                : completedZeroBootstrap
                  ? 'bridge_setup'
                  : 'undetermined',
            local_bridge_state: intentIdentity.status,
            studio_count: rooms.length,
            action: 'resolve',
            outcome: 'success'
          });
        }
        if (decision.kind === 'choose_studio') {
          yield* checkpoint('loading-organizations');
          let replicaOrganizations = yield* unwrap(
            timeBootHop('root.list-organizations', listOrganizations(token), attemptLane)
          );
          // QA Hive ITG 2026-08-10 (residual after the room adoption): the ORG
          // created by ensureDefaultOrgAndRoom can be missing from this replica
          // list — the switcher then labels the studio "Create a studio" until
          // a reload. Wait, bounded, for the adopted room's org to become
          // visible; on deadline the cosmetic fallback degrades gracefully.
          if (bootstrapCreatedRoomId !== undefined) {
            const adoptedRoom = rooms.find(
              (room) => toHexFromId(room.id) === bootstrapCreatedRoomId
            );
            const adoptedOrgId = adoptedRoom ? toHexFromId(adoptedRoom.organizationId) : undefined;
            if (adoptedOrgId !== undefined) {
              replicaOrganizations = yield* unwrap(
                awaitBootstrapOrgVisibility({
                  createdOrgId: adoptedOrgId,
                  organizations: replicaOrganizations,
                  listOrganizations: () => listOrganizations(token, { fresh: true }),
                  orgIdOf: (organization) => toHexFromId(organization.id),
                  timeoutMs: AUTO_LINK_ROOM_SYNC_TIMEOUT_MS,
                  pollIntervalMs: AUTO_LINK_ROOM_SYNC_POLL_INTERVAL_MS,
                  signal
                })
              );
              if (!isCurrent()) return;
            }
          }
          const organizations = mergeAuthoritativeBootstrapOrganizations(
            replicaOrganizations,
            authoritativeOwnedOrganizations
          );
          if (!isCurrent()) return;
          produce((draft) => {
            const roomsMap = toRoomRecordMap(rooms);
            draft.roomRecords = roomsMap;
            // Same completeness contract as the enter_studio write below.
            draft.organizations = completeRoomOrganizations(
              roomsMap,
              toOrganizationMap(organizations, studioUser),
              draft.organizations
            );
            draft.synchronizer.hasRegistryLoaded = true;
            draft.synchronizer.isRegistryLoading = false;
          });
          resolveLoadedLists();
          // ENG-4175: intercept BEFORE dropping a first-login user into the
          // Studio picker. Sits after hasRegistryLoaded is set so the
          // BootOverlay dismisses on the wizard route.
          if (goToPrequalIfGated()) return;
          // This hook re-ran four times on a single boot in CI, sending the user
          // back to the picker from a room each time, so the launch terminal was
          // never committed. The decision and the two preferences this hook
          // depends on are what distinguish "the account really has no single
          // Studio" from "this hook is re-entering because a preference it
          // depends on is being rewritten underneath it".
          console.info(
            `[RootBootstrap] → picker decision=${decision.kind} ` +
              `lastRoom=${lastRoomId ?? 'none'} lastRoomLoaded=${lastRoomLoaded} ` +
              `lastAccount=${lastAccountId ?? 'none'} path=${window.location.pathname}`
          );
          navigate(`/${Paths.selectStudio}${window.location.search}`, {
            state: toAccountStudioPickerState(
              decision.mode,
              authoritativeInventoryRooms,
              intentIdentity.status === 'linked' ? intentIdentity.agentId : undefined,
              authoritativeCapabilities
            )
          });
          setReady(true);
          return;
        }

        if (decision.kind !== 'enter_studio') {
          throw new ApiErrorClass('Account bootstrap did not settle to a Studio.');
        }

        yield* checkpoint('loading-organizations');
        // MERGE DECISION (ENG-4720 x ENG-4722): ENG-4720's rename and its
        // bounded org-visibility wait win; Spec 4's timeBootHop is re-applied to
        // the same call so the org read stays attributed on this path.
        let replicaOrganizations = yield* unwrap(
          timeBootHop('root.list-organizations', listOrganizations(token), attemptLane)
        );
        // QA Hive ITG 2026-08-10: same bounded org-visibility wait as the
        // choose_studio branch above — the enter_studio path is the one the
        // Hive default-studio bootstrap actually lands on. The auto-link and
        // linked-studio paths do not wait here: their orgs arrive through the
        // authoritative primary inventory merged below
        // (mergeAuthoritativeBootstrapOrganizations), so a replica gap on
        // those paths is closed without a poll.
        if (bootstrapCreatedRoomId !== undefined) {
          const adoptedRoom = rooms.find((room) => toHexFromId(room.id) === bootstrapCreatedRoomId);
          const adoptedOrgId = adoptedRoom ? toHexFromId(adoptedRoom.organizationId) : undefined;
          if (adoptedOrgId !== undefined) {
            replicaOrganizations = yield* unwrap(
              awaitBootstrapOrgVisibility({
                createdOrgId: adoptedOrgId,
                organizations: replicaOrganizations,
                listOrganizations: () => listOrganizations(token, { fresh: true }),
                orgIdOf: (organization) => toHexFromId(organization.id),
                timeoutMs: AUTO_LINK_ROOM_SYNC_TIMEOUT_MS,
                pollIntervalMs: AUTO_LINK_ROOM_SYNC_POLL_INTERVAL_MS,
                signal
              })
            );
            if (!isCurrent()) return;
          }
        }
        const organizations = mergeAuthoritativeBootstrapOrganizations(
          replicaOrganizations,
          authoritativeOwnedOrganizations
        );
        if (!isCurrent()) return;
        const roomsMap = toRoomRecordMap(rooms);
        const roomId = toHexFromId<Room['id']>(decision.studio.value.id);
        if (roomId === undefined) {
          throw new ApiErrorClass('The selected Studio has an invalid identity.');
        }
        const roomRecord = roomsMap[roomId];
        produce((draft) => {
          draft.roomRecords = roomsMap;
          // An incomplete org read must not discard another writer's orgs for
          // rooms this snapshot keeps (registry-completeness, ENG-4695).
          draft.organizations = completeRoomOrganizations(
            roomsMap,
            toOrganizationMap(organizations, studioUser),
            draft.organizations
          );
          draft.synchronizer.hasRegistryLoaded = true;
          draft.synchronizer.isRegistryLoading = false;
        });

        resolveLoadedLists();

        // ENG-4175: intercept BEFORE dropping a first-login user into their
        // room (covers the auto-linked fresh-signup path too). Sits after
        // hasRegistryLoaded is set so the BootOverlay dismisses on the wizard
        // route; member loading is skipped — the wizard doesn't need it.
        if (goToPrequalIfGated()) return;

        if (roomRecord !== undefined) {
          yield* checkpoint('loading-members');
          try {
            const { invites, members } = yield* unwrap(
              loadMembers(roomId, roomRecord.organizationId, token)
            );
            if (!isCurrent()) return;
            produce((draft) => {
              draft.roomInviteRecords = invites;
              draft.roomMembershipRecords = members;
            });
          } catch (cause) {
            if (!isCurrent()) return;
            const memberError =
              cause instanceof Error
                ? cause
                : new ApiErrorClass('Studio members failed with an unknown cause.', { cause });
            console.warn('Unable to load Studio members during bootstrap.', memberError);
            produce((draft) => {
              draft.roomInviteRecords = [];
              draft.roomMembershipRecords = [];
            });
          }
        }

        const currentIdentity = useIdentityStore.getState().identity;
        const currentLinkedAgentID =
          currentIdentity.status === 'linked' ? currentIdentity.agentId : undefined;
        const hasLocalAgent = shouldUseAuthoritativeLinkedRoomBoot(
          roomId,
          currentLinkedAgentID,
          Object.keys(roomRecord?.agents ?? {}).length,
          verifiedInitialRoomId
        );
        const path =
          isElectronApp() && hasLocalAgent ? Paths.room.boot.resolve : Paths.room.resolve;
        if (!isCurrent()) return;
        // Setup rollback: a create-and-link that quit AFTER its link committed but
        // BEFORE its durable record was cleared boots straight into the Studio
        // here and never mounts the picker that would retire that record. Left
        // in place, it would later read as an orphan the moment this Studio's
        // last Bridge is unlinked, and Bridge Setup would delete a Studio that
        // succeeded. Settle it now against the authoritative inventory: the
        // record's Studio is this linked one (kept, record cleared), another
        // Studio still unlinked (rolled back), or already gone. Fire-and-forget
        // — boot never waits on cleanup, and its own failure is non-fatal.
        void reconcilePendingAccountStudioCreate({
          accountId,
          expectedAgentId: currentLinkedAgentID,
          token
        }).catch((reconcileCause: unknown) => {
          logger.warning(
            'Unable to reconcile an interrupted Studio create at boot.',
            reconcileCause
          );
        });
        yield* checkpoint('navigating');
        navigate(`${path(roomId)}${window.location.search}`);
        setReady(true);
      } catch (cause) {
        if (!isCurrent()) return;
        // Spec 4 (D1): a failure that is still the current attempt is the one
        // the table should read as `error`. A superseded attempt falls through
        // to the finally below and closes as `cancelled`, because its failure
        // no longer describes anything the user saw.
        closeAttempt('error', 'boot-failed');
        trackBootFailed(cause);
        setErrorState({
          accountId,
          error:
            cause instanceof ApiErrorClass
              ? cause
              : new ApiErrorClass('Unable to load account.', { cause })
        });
      } finally {
        // Spec 4 (D1) x MERGE DECISION (ENG-4720 x ENG-4722): ONE close for the
        // whole attempt. The pre-merge hook closed the lane by hand at each
        // terminal — a dozen `closeAttempt('ok', '<where>')` calls — and
        // ENG-4720's restructure of this flow would have silently invalidated
        // that: a newly added early return is invisible to review and leaks an
        // open lane, which the recorder can then only sweep as `cancelled` at
        // document end. checkpoint() already records WHERE the boot got to, so
        // the only thing a terminal still has to say is HOW — decided here,
        // once, for every return and for the fall-through alike. closeAttempt
        // is idempotent, so the explicit error close above still wins.
        closeAttempt(isCurrent() ? 'ok' : 'cancelled');
      }
    },
    [
      auth0User?.sub,
      bootError,
      completeWithoutAutoLink,
      coordinator,
      getAccessTokenSilently,
      getZeroRoomAutoLinkRunPhase,
      lastAccountId,
      lastAccountLoaded,
      lastRoomId,
      lastRoomLoaded,
      requestZeroRoomAutoLink,
      studioBootstrapAnalytics,
      studioUser?.id,
      // ENG-4175: re-run once the prequal gate resolves loading→show/skip so a
      // held first-login bootstrap proceeds (to the wizard, or onward).
      prequalGate
    ]
  );

  const error =
    errorState !== undefined && errorState.accountId === auth0User?.sub
      ? errorState.error
      : undefined;
  return { accountId: auth0User?.sub, error, isReady, linkedAgentID, watchdogEnabled };
};
